import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { TKeyValuePair } from 'library/ts/types/TKeyValuePair';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IImportsAvia } from 'tag/ts/tag/interfaces/config/imports/IImportsAvia';
import { IAviaExtension } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaExtension';
import { TExtensionBuilder } from 'tag/ts/tag/player/adapters/avia/types/TExtensionBuilder';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description Base class for VTG Avia platform capability extensions
 *
 * **Platform Extension Framework:**
 * Provides standardized infrastructure for creating platform-specific VTG Avia extensions
 * that enhance video playback capabilities on Smart TVs, gaming consoles, and set-top boxes.
 * Extensions integrate with VTG's namespace system for platform optimization.
 *
 * **Extension Architecture Pattern:**
 * - **Conditional Activation**: `shouldBeUsed()` determines platform-specific activation
 * - **Namespace Integration**: Registers extension with VTG's global namespace system
 * - **Package Management**: Loads platform-specific code from NPM packages or DOM
 * - **Smart Tag Integration**: Provides access to Smart Tag business logic and device detection
 *
 * **Platform Detection Strategy:**
 * Extensions use sophisticated device detection to determine when platform-specific
 * capabilities should be activated:
 * - **User Agent Analysis**: Browser/platform identification via navigator.userAgent
 * - **API Availability**: Platform-specific API presence detection (webOS, Tizen, etc.)
 * - **Device Parameters**: Smart Tag device classification and embed type validation
 *
 * **Extension Lifecycle:**
 * 1. **Construction**: Extension instantiation with Smart Tag context
 * 2. **Capability Check**: `shouldBeUsed()` evaluates platform requirements
 * 3. **Registration**: Extension registered with VTG namespace if applicable
 * 4. **Integration**: Platform capabilities become available to VTG player
 *
 * **Supported Platform Categories:**
 * - **Smart TV Platforms**: LG webOS, Samsung Tizen for TV-optimized playback
 * - **Gaming Consoles**: Xbox One for console-specific streaming optimization
 * - **Cable Platforms**: Comcast X1 for set-top box integration
 * - **Content Protection**: HDCP for DRM support across supporting devices
 *
 * **Business Impact:**
 * Enables Smart Tag to leverage platform-specific optimizations while maintaining
 * consistent business logic across diverse deployment environments.
 */
export abstract class AviaExtension extends AbstractTagClass implements IAviaExtension {

	package: TExtensionBuilder;

	#loaded: boolean;

	abstract namespaceKey: string;

	get namespaceBase(): IImportsAvia {
		return this?.tag?.model?.config?.tag?.Imports?.params?.avia;
	}

	get extensionConfig(): IImportable {
		return this?.namespaceBase?.[ this.namespaceKey ];
	}

	get namespaceComplete(): string {
		return this?.extensionConfig?.namespace;
	}

	get enabled(): boolean {
		return this?.extensionConfig?.enabled;
	}

	get preloadedExtension(): TExtensionBuilder {
		// sanity check
		if ( this.utils.Type.isEmpty( this.namespaceComplete ) || !this.utils.Type.isString( this.namespaceComplete ) ) {
			return undefined;
		}

		// get namespace reference from DOM
		const preloadedPackage = this.utils.Object.getDeepVal( window, this.namespaceComplete );

		if ( !this.utils.Type.isNullOrUndefined( preloadedPackage ) ) {
			// return the factory builder function for this extension
			return preloadedPackage?.extension;
		}

		return undefined;
	}

	get builder(): TExtensionBuilder {
		this?.logger?.log( 'getExtensionBuilder', this );
		let builderFunction = undefined;

		const preloadedExtension = this.preloadedExtension;
		this?.logger?.log( 'preloadedExtension', preloadedExtension );

		// Provide the extension builder if one already exists in DOM
		if ( !this.utils.Type.isNullOrUndefined( preloadedExtension ) ) {
			this?.logger?.log( 'Extension was preloaded: ', 'preloadedExtension =', preloadedExtension );
			builderFunction = preloadedExtension;
		}
		// no DOM entry for extension is found, provide package version instead
		else if ( !this.utils.Type.isNullOrUndefined( this.package ) ) {
			this?.logger?.log( 'Extension package was used: ', 'this.package =', this.package );
			builderFunction = this.package;
		}

		// Send warning if no extension config can be provided.
		if ( this.utils.Type.isNullOrUndefined( builderFunction ) ) {
			this?.logger?.warn( 'Extension was neither imported nor loaded', this.namespaceComplete, this.package );
		}

		return builderFunction;
	}

	protected constructor( argument: ITagAbstractClassArgument ) {
		super( argument );
	}

	shouldBeUsed(): boolean {
		if ( this.#loaded ) {
			this?.logger?.log( 'Extension already loaded' );
		}

		if ( this?.enabled !== true ) {
			this?.logger?.log( 'Extension is not enabled' );
		}

		return this?.enabled === true && !this.#loaded;
	}

	register(): Promise<void> {
		this?.logger?.log( 'register', 'this', this, 'Extension package: ', 'this.package =', this.package );
		if ( !this.package ) {
			return Promise.resolve();
		}
		if ( !window.avia?.use ) {
			this?.logger?.warn( 'window.avia.use is unavailable' );
			return Promise.reject( 'window.avia.use is unavailable' );
		}

		return window.avia?.use?.( this.builder( this.getExtensionParams() ) )?.then( () => {
			this.#loaded = true;
		} );
	}

	getExtensionParams(): TKeyValuePair {
		return {
			debug: this.runtimeVO?.debug?.player
		};
	}
}